import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon } from 'lucide-react';
import {
  CalendarScheduleIcon,
  DateRangePicker,
  Popover,
  PopoverContent,
  PopoverTrigger,
  standardDateRangePresets,
} from '@noor/ui';
import type { ModerationListSearch } from '@/entities/moderation';
import {
  formatDatePillLabel,
  toDateParams,
  toDateRangeValue,
} from '../../lib/date-filter';

interface DateRangePillProps {
  dateFrom: string | undefined;
  dateTo: string | undefined;
  onParamsChange: (next: Partial<ModerationListSearch>) => void;
}

/**
 * Date-range pill (Figma filter row): popover range picker with presets.
 * Reads entirely from the URL (no local draft) — the picker commits on
 * Apply, the X clears both bounds.
 */
export function DateRangePill({ dateFrom, dateTo, onParamsChange }: DateRangePillProps) {
  const { t, i18n } = useTranslation('moderation');
  const [open, setOpen] = useState(false);
  const presets = useMemo(
    () =>
      standardDateRangePresets({
        yesterday: t('filters.yesterday', 'Вчера'),
        today: t('filters.today', 'Сегодня'),
        thisWeek: t('filters.thisWeek', 'Эта неделя'),
        thisMonth: t('filters.thisMonth', 'Этот месяц'),
        thisYear: t('filters.thisYear', 'Этот год'),
      }),
    [t],
  );
  const appliedRange = useMemo(
    () => toDateRangeValue(dateFrom, dateTo),
    [dateFrom, dateTo],
  );
  const label = formatDatePillLabel(
    appliedRange,
    presets,
    t('filters.datePlaceholder', 'Выберите период'),
  );

  const clear = () => onParamsChange({ dateFrom: undefined, dateTo: undefined });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('filters.date', 'Период подачи')}
          className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground transition-colors outline-none hover:border-ring/40 focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <CalendarScheduleIcon className="size-5 shrink-0 text-primary" />
          <span className="whitespace-nowrap tabular-nums">{label}</span>
          {appliedRange !== undefined && (
            <span
              role="button"
              tabIndex={0}
              aria-label={t('filters.clearDate', 'Сбросить период')}
              onClick={(event) => {
                event.stopPropagation();
                clear();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  clear();
                }
              }}
              className="flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <XIcon className="size-4" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-[20px] p-4">
        <DateRangePicker
          value={appliedRange}
          presets={presets}
          locale={i18n.language}
          onApply={(range) => {
            onParamsChange(toDateParams(range));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
